import React from 'react';
import { ArrowPathIcon, CheckCircleIcon, SparklesIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import Modal from '../../../../../components/ui/Modal';
import { formatGapTime } from '../../hooks/useTranscriptGaps';

const isLikelyMissing = (gap) => gap.gap_type === 'possible_missing_content';

function GapAction({ gap, idx, fixer }) {
  const { fixOne, startingIdx, isFixingAll } = fixer;
  const base = 'w-full mt-1 inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg';

  if (gap._status === 'done') {
    return <div className={`${base} bg-green-100 border border-green-300 text-green-700`}><CheckCircleIcon className="h-4 w-4" /> Regenerated successfully</div>;
  }
  if (gap._status === 'failed') {
    return (
      <button type="button" onClick={() => fixOne(gap, idx)} disabled={isFixingAll} className={`${base} bg-red-100 border border-red-300 text-red-700 hover:bg-red-200 disabled:opacity-50`}>
        <WrenchScrewdriverIcon className="h-4 w-4" /> Retry (failed)
      </button>
    );
  }
  const processing = gap._status === 'processing';
  const starting = startingIdx === idx;
  const tone = processing
    ? 'bg-blue-100 border border-blue-300 text-blue-700 cursor-default'
    : isLikelyMissing(gap)
      ? 'bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50'
      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50';
  return (
    <button type="button" onClick={() => fixOne(gap, idx)} disabled={isFixingAll || starting || processing} className={`${base} ${tone}`}>
      {processing || starting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <WrenchScrewdriverIcon className="h-4 w-4" />}
      {processing ? 'Regenerating...' : starting ? 'Starting...' : 'Regenerate section'}
    </button>
  );
}

function GapRow({ gap, idx, fixer }) {
  const ratio = gap.audio_energy_ratio;
  return (
    <div className={`border rounded-lg p-4 ${isLikelyMissing(gap) ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${isLikelyMissing(gap) ? 'bg-amber-200 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>
            {isLikelyMissing(gap) ? 'Likely missing' : 'Silent pause'}
          </span>
          <span className="text-sm font-medium text-gray-700">{formatGapTime(gap.start_time)} - {formatGapTime(gap.end_time)}</span>
        </div>
        <span className="text-xs text-gray-500">{Math.round(gap.duration)}s</span>
      </div>

      {ratio !== null && ratio >= 0 && (
        <div className="mb-2">
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${ratio > 0.5 ? 'bg-amber-500' : ratio > 0.15 ? 'bg-amber-300' : 'bg-gray-300'}`}
              style={{ width: `${Math.round(ratio * 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Speech: {Math.round(ratio * 100)}%</p>
        </div>
      )}

      {(gap.before_segment_end_text || gap.after_segment_start_text) && (
        <div className="text-xs text-gray-500 mb-2 space-y-1">
          {gap.before_segment_end_text && <p className="truncate">...{gap.before_segment_end_text}</p>}
          {gap.after_segment_start_text && <p className="truncate">{gap.after_segment_start_text}...</p>}
        </div>
      )}

      <GapAction gap={gap} idx={idx} fixer={fixer} />
    </div>
  );
}

export default function FixSectionsModal({ fixer }) {
  const { isOpen, close, gaps, isDetecting, isFixingAll, fixAll } = fixer;
  const list = gaps?.gaps || [];

  let body = null;
  if (isDetecting) {
    body = (
      <div className="flex flex-col items-center py-10">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-amber-500 mb-3" />
        <p className="text-gray-600">Analyzing transcript for gaps...</p>
        <p className="text-xs text-gray-400 mt-1">Checking audio for speech in each gap</p>
      </div>
    );
  } else if (gaps && list.length === 0) {
    body = (
      <div className="flex flex-col items-center py-10">
        <CheckCircleIcon className="h-12 w-12 text-green-500 mb-3" />
        <p className="text-gray-700 font-medium">No gaps detected</p>
        <p className="text-sm text-gray-500 mt-1">The transcript appears to be complete</p>
      </div>
    );
  } else if (gaps) {
    body = (
      <div className="max-h-[60vh] overflow-y-auto space-y-3">
        <p className="text-sm text-gray-600">
          Found <strong>{gaps.gap_count}</strong> gap(s)
          {gaps.possible_missing_count > 0 && <span className="text-amber-600">: {gaps.possible_missing_count} likely missing content</span>}
          {gaps.silent_pause_count > 0 && <span className="text-gray-500">, {gaps.silent_pause_count} silent pauses</span>}
        </p>
        {list.length > 1 && (
          <button
            type="button"
            onClick={fixAll}
            disabled={isFixingAll}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 disabled:opacity-60"
          >
            {isFixingAll ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <SparklesIcon className="h-5 w-5" />}
            {isFixingAll ? `Regenerating all (${list.length} gaps)...` : `Regenerate all (${list.length} gaps)`}
          </button>
        )}
        {list.map((gap, idx) => <GapRow key={idx} gap={gap} idx={idx} fixer={fixer} />)}
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title="Fix missing sections">
      {body}
    </Modal>
  );
}
